import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SecureMedia } from "@/components/SecureMedia";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Send, Paperclip, Loader2, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";

// Shared chat thread + composer. selfRole is "CUSTOMER" or "ADMIN".
export const ChatWindow = ({ messages, selfRole, onSend, sending }) => {
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const sendText = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setText("");
    await onSend({ content: t });
  };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "chat");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onSend({ file_id: data.file_id });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="flex-1 overflow-y-auto space-y-3 px-1 py-2" data-testid="chat-message-list">
        {messages.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-10">No messages yet. Start the conversation.</p>
        )}
        {messages.map((m) => {
          const mine = m.sender_role === selfRole;
          const read = selfRole === "CUSTOMER" ? m.read_by_admin : m.read_by_customer;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.id}`}>
              <div className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-3.5 py-2.5 ip-card-shadow ${mine ? "bg-brand text-white rounded-br-sm" : "bg-white border border-border rounded-bl-sm"}`}>
                {m.type !== "TEXT" && m.file_id && (
                  <div className="mb-1"><SecureMedia fileId={m.file_id} type={m.type} fileName={m.file_name} testId={`chat-media-${m.id}`} dark={mine} /></div>
                )}
                {m.content && <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>}
                <div className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                  <span>{formatDateTime(m.created_at)}</span>
                  {mine && (read ? <CheckCheck className="h-3.5 w-3.5" data-testid={`chat-read-${m.id}`} /> : <Check className="h-3.5 w-3.5" />)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={sendText} className="flex items-center gap-2 sticky bottom-0 bg-background py-2 border-t border-border/60">
        <input ref={fileRef} type="file" className="hidden" accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" onChange={pick} data-testid="chat-file-input" />
        <Button type="button" variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={() => fileRef.current?.click()} disabled={uploading || sending} data-testid="chat-attach-button">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </Button>
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="h-11" data-testid="chat-input" />
        <Button type="submit" className="h-11 w-11 shrink-0 p-0" disabled={sending || uploading || !text.trim()} data-testid="chat-send-button">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
};
